import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { queriesApi } from '../services/queriesService'
import { transformForTodayData } from '../utils/contentfulTransformers'

/**
 * Hook para obtener y procesar el contenido "para hoy" de Contentful
 * Utiliza Tanstack Query para cache de 5 días y mejores prácticas
 *
 * @returns {Object} Objeto con loading, error y datos procesados para hoy
 */
export const useContentfulForToday = () => {
	// Query con Tanstack Query
	const {
		data: rawData,
		isLoading,
		error,
		refetch,
		isStale,
		isRefetching
	} = useQuery({
		queryKey: ['contentful', 'fortoday'] as const,
		queryFn: queriesApi.getForToday,
		staleTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		cacheTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		retry: 2,
		retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchOnWindowFocus: false,
		refetchOnReconnect: true
	})

	// Manejar errores
	if (error) {
		console.error('Error al obtener datos de ForToday desde Contentful:', error)
	}

	// Procesar datos usando memoización
	const processedData = useMemo(() => {
		if (!rawData || isLoading || error) {
			return {
				medicineTemplate: false,
				planCareContent: false,
				headForToday: false,
				contForToday: false
			}
		}

		try {
			return transformForTodayData(rawData)
		} catch (transformError) {
			console.error('Error al transformar datos de ForToday:', transformError)
			return {
				medicineTemplate: false,
				planCareContent: false,
				headForToday: false,
				contForToday: false
			}
		}
	}, [rawData, isLoading, error])

	// Información del cache
	const cacheInfo = useMemo(
		() => ({
			isStale,
			isRefetching,
			lastFetch: rawData ? new Date() : null,
			cacheExpiry: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)
		}),
		[isStale, isRefetching, rawData]
	)

	return {
		// Datos principales (compatibilidad con hook original)
		loading: isLoading,
		error,
		...processedData, // medicineTemplate, planCareContent, headForToday, contForToday

		// Funcionalidades adicionales
		refetch,
		isStale,
		isRefetching,
		cacheInfo,
		rawData
	}
}

export default useContentfulForToday
